import '@animxyz/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';

import SSOLink from '~/components/SSOLink';
import useFeature from '~/hooks/useFeature';
import useHostingInfo from '~/hooks/useHostingInfo';

import { QuickLink } from '../../scenes/Overview/utils/types';
import MultipleChoiceSsoDialog from './MultipleChoiceSsoDialog';
import useServerEmailChoices from './ServerEmailChoices';

export type QuickLinksProps = {
  links: QuickLink[];
};
const QuickLinks = ({ links: quickLinks }: QuickLinksProps) => {
  const { t } = useTranslation('quickLinks');
  const [serverEmailIsOpen, setServerEmailIsOpen] = useState(false);

  const { hostingAccount, hostingId, isDisabled, isPending } = useHostingInfo();

  const [hasCpanel, , isClientReady] = useFeature('cpanel');
  const isReseller = hostingAccount?.data?.type === 'reseller';

  const choices = useServerEmailChoices(hostingId, serverEmailIsOpen);

  const handleServerEmailClose = () => {
    setServerEmailIsOpen(false);
  };

  const handleServerEmailClick = () => {
    setServerEmailIsOpen(true);
  };

  if (!isClientReady || isPending) {
    return (
      <Card data-testid="quick-links">
        <CardHeader title={t('quickLinks.cardTitle')} />
        <CardContent>{t('quickLinks.loading')}</CardContent>
      </Card>
    );
  }

  if (quickLinks.length === 0) {
    // Hide the entire card if there are no quick links
    return null;
  }

  return (
    <Card data-testid="quick-links">
      <CardHeader title={t('quickLinks.cardTitle')} />
      <CardContent>
        <Stack spacing={2}>
          {quickLinks.map(({ name, type, queryParams }: QuickLink) => (
            <SSOLink
              key={`sso-link-${name}`}
              fluid
              type={type!}
              {...(queryParams && { queryParams })}
            >
              {t(`quickLinks.links.${name}`)}
            </SSOLink>
          ))}
          {hasCpanel && !isReseller && (
            <>
              <MultipleChoiceSsoDialog
                isOpen={serverEmailIsOpen}
                onClose={handleServerEmailClose}
                title={t('serverEmail.title')}
                description={t('serverEmail.description')}
                choices={choices}
              />
              <Button
                variant="outlined"
                onClick={handleServerEmailClick}
                data-testid="email-sso-button"
                disabled={isDisabled}
              >
                {t('serverEmail.buttonLabel')}
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default QuickLinks;
